import { InstancedMesh, Mesh } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { createEntityFactory, playAnimation } from '@modules/standard';
import { World } from 'miniplex';

import { Entity as BaseEntity, MeshType } from '..';

import { UnitPanelSystem } from './UnitPanelSystem';

export type SupplementaryVisibleEntity = {
  SupplementaryVisible?: true;
};

type Entity = SupplementaryVisibleEntity & BaseEntity;

export class SupplementaryVisibleSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('SupplementaryVisibleSystem');

  private readonly unitPanelSystem;

  private readonly danjiMeshes;
  private readonly supplementaryVisibles;
  private readonly createEntity;

  private wasVisible = false;

  private readonly options = {
    fadeTo: 0.5,
    fadeDuration: 0.3,
  };

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [UnitPanelSystem.INJECTABLE_TOKEN]: unitPanelSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [UnitPanelSystem.INJECTABLE_TOKEN]: UnitPanelSystem;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    this.unitPanelSystem = unitPanelSystem;

    this.danjiMeshes = world.with('DanjiMeshes');
    this.supplementaryVisibles = world.with('SupplementaryVisible', 'TransformNode');

    this.danjiMeshes.onEntityAdded.subscribe(this.initializeDanjiMeshes);

    this.createEntity = createEntityFactory(world);

    this.operations = [
      {
        group: 'viewMatrixIdle',
        run: this.whenViewMatrixIdle,
        after: [unitPanelSystem['whenViewMatrixIdle']],
      },
    ];
  }

  private readonly initializeDanjiMeshes = (entity: (typeof this.danjiMeshes.entities)[number]) => {
    entity.DanjiMeshes.danjiMeshes
      .filter((dm) =>
        [
          MeshType.GIS_BUILDING,
          MeshType.FACILITY_TREE_NORMAL,
          MeshType.FACILITY_TREE_BUSH,
          MeshType.FACILITY_TREE_FLOWER,
          MeshType.FACILITY_PARKING_ENTERANCE,
          MeshType.FACILITY_BUILDING,
        ].includes(dm.type)
      )
      .forEach((danjiMeshes) => {
        danjiMeshes.meshes.forEach((m) => {
          this.createEntity({
            TransformNode: m,
            SupplementaryVisible: true,
          });
        });
      });
  };

  private whenViewMatrixIdle = () => {
    const isVisible = !this.unitPanelSystem.isVisible;
    if (isVisible === this.wasVisible) return;

    const targets = new Set(
      this.supplementaryVisibles.entities.flatMap((e) => {
        const n = e.TransformNode;
        if (n instanceof Mesh) return [n];
        if (n instanceof InstancedMesh) return [n.sourceMesh];
        return [];
      })
    );

    const fadeFrom = isVisible ? this.options.fadeTo : 1;
    const fadeTo = isVisible ? 1 : this.options.fadeTo;
    const remaining = Math.abs(fadeTo - fadeFrom);
    const duration = remaining * this.options.fadeDuration;

    playAnimation(
      {
        target: Array.from(targets),
        property: {
          targetProperty: 'visibility',
          keyFrame: {
            duration: duration,
            from: fadeFrom,
            to: fadeTo,
          },
        },
      },
      undefined,
      true
    );

    this.wasVisible = isVisible;
  };

  readonly operations: SystemOperationDefinition[];
}
